/**
 * Turnstile Configuration Module
 * 
 * Manages Cloudflare Turnstile keys for development and production environments.
 * Automatically uses test keys in development and real keys in production.
 * 
 * @module turnstileConfig
 */

/**
 * Cloudflare test site keys for development
 * https://developers.cloudflare.com/turnstile/troubleshooting/testing/
 */
const TURNSTILE_TEST_SITE_KEYS = {
    VISIBLE_ALWAYS_PASS: '1x00000000000000000000AA',
    INVISIBLE_ALWAYS_PASS: '2x00000000000000000000AA',
    FORCE_CHALLENGE: '3x00000000000000000000AA',
}

/**
 * Check if running in development mode
 * @returns {boolean} True if development
 */
const isDevelopment = (): boolean => {
    return (
        process.env.NODE_ENV === 'development' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('.local')
    )
}

/**
 * Get Turnstile site key based on environment
 * 
 * Development: Uses Cloudflare test key (always passes)
 * Production: Uses real key from environment variable
 * 
 * @returns {string} Turnstile site key
 */
export const getTurnstileSiteKey = (): string => {
    if (isDevelopment()) {
        console.log('🔧 [DEV MODE] Using Turnstile test site key')
        return TURNSTILE_TEST_SITE_KEYS.VISIBLE_ALWAYS_PASS
    }

    const productionKey = process.env.REACT_APP_TURNSTILE_SITE_KEY

    if (!productionKey) {
        console.error('❌ REACT_APP_TURNSTILE_SITE_KEY not configured for production!')
        console.warn('⚠️  Falling back to test key (NOT RECOMMENDED for production)')
        return TURNSTILE_TEST_SITE_KEYS.VISIBLE_ALWAYS_PASS
    }

    return productionKey
}

/**
 * Export test keys for development use
 */
export const TEST_SITE_KEYS = TURNSTILE_TEST_SITE_KEYS

/**
 * Export development check for conditional rendering
 */
export const IS_DEVELOPMENT = isDevelopment()

/**
 * Usage example:
 * 
 * import { getTurnstileSiteKey } from '../config/turnstile'
 * 
 * <Turnstile
 *     siteKey={getTurnstileSiteKey()}
 *     onVerify={handleVerify}
 * />
 */

